import Linkify from 'linkify-it';
import flatMap from 'lodash/flatMap';

function updateStringSlice(text, startIdx, endIdx, updateFn) {
  return [
    text.slice(0, startIdx),
    updateFn(text.slice(startIdx, endIdx)),
    text.slice(endIdx),
  ].join('');
}

function renderedMentions(allMentions, text) {
  return allMentions.reduce((mentions, mention) => {
    if (mention.startIndex === 0 && mention.length === 0) {
      return mentions;
    }
    const joinCandidate = mentions[mentions.length - 1];
    if (
      joinCandidate &&
      joinCandidate.startIndex === mention.startIndex &&
      joinCandidate.length === mention.length
    ) {
      return mentions.slice(0, -1).concat({
        startIndex: joinCandidate.startIndex,
        length: joinCandidate.length,
        // assume the mention text is the team name
        userId: text.slice(
          mention.startIndex + 1, // without the @-character
          mention.startIndex + mention.length,
        ),
        type: 'team',
      });
    }
    return mentions.concat(mention);
  }, []);
}

/**
 * Returns a markdown representation of the text and mentions, that can be sent
 * to react-mentions. The `displayText`
 *
 * @param  {string}   text     The body of the message, as plain text
 * @param  {mentions} mentions An array of mentions, some that won't be shown
 *                             (startIndex === 0 and length === 0), some
 *                             overlapping each other completely (team mentions)
 * @return {string}            Markdown, which react-mentions can work with
 */
export function getMarkdown(text, mentions) {
  return renderedMentions(mentions, text).reduceRight(
    (partiallyTransformedText, mention) =>
      updateStringSlice(
        partiallyTransformedText,
        mention.startIndex,
        mention.startIndex + mention.length,
        displayText => `@[${displayText}](${mention.userId})`,
      ),
    text,
  );
}

function mergeMentionsAndLinks(mentions, links) {
  const mentionsWithEnd = mentions.map(mention => ({
    startIndex: mention.startIndex,
    lastIndex: mention.startIndex + mention.length,
    url:
      mention.type === 'team'
        ? `/users?team=${mention.userId}`
        : `/users/${mention.userId}/show`,
  }));
  const nonConflictingLinks = links
    .map(link => ({
      startIndex: link.index,
      lastIndex: link.lastIndex,
      url: link.url,
    }))
    .filter(link =>
      mentionsWithEnd.every(
        mention =>
          link.lastIndex <= mention.startIndex ||
          link.startIndex >= mention.lastIndex,
      ),
    );

  return mentionsWithEnd
    .concat(nonConflictingLinks)
    .sort((a, b) => a.startIndex - b.startIndex);
}

const linkify = new Linkify();

/**
 * Slice up the provided text at the mentions boundaries and for any identified
 * links, so that it can be rendered with a simple `map`.
 * @param  {string}   text     The body of the message, as plain text
 * @param  {mentions} mentions An array of mentions, some that won't be shown
 *                             (startIndex === 0 and length === 0), some
 *                             overlapping each other completely (team mentions)
 * @return {parts}             An array of structs with `text` and sometimes `href`
 */
export function sliceUp(text, mentions) {
  // assume mentions are sorted by startIndex
  // assume mentions do not overlap, or overlap completely

  const links = linkify.match(text) || [];
  const allInteractiveParts = mergeMentionsAndLinks(
    renderedMentions(mentions, text),
    links,
  );

  let partsEndIndex = 0;
  const parts = flatMap(allInteractiveParts, part => {
    const before = text.slice(partsEndIndex, part.startIndex);
    partsEndIndex = part.lastIndex;
    const partText = text.slice(part.startIndex, partsEndIndex);
    return [
      { text: before },
      {
        text: partText,
        href: part.url,
      },
    ];
  });

  return parts
    .concat({ text: text.slice(partsEndIndex) })
    .filter(ea => ea.text.length > 0);
}
